(function () {
  const STORAGE_KEY = 'zasuwa_recent_products';
  const MAX_STORED = 7;
  const MAX_VISIBLE = 6;
  const MOBILE_QUERY = window.matchMedia('(max-width: 749px)');

  const state = {
    widget: null,
    button: null,
    buttonFace: null,
    buttonImage: null,
    badge: null,
    panel: null,
    list: null,
    open: false,
    visible: false,
    renderQueued: false,
    renderTimer: null,
    layoutQueued: false,
    layoutTimer: null,
    introTimer: null,
    panelHideTimer: null,
    lastSignature: '',
  };

  function safeParse(json) {
    try {
      const parsed = JSON.parse(json);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  function storageAvailable() {
    try {
      const key = '__zasuwa_recent_history_test__';
      sessionStorage.setItem(key, '1');
      sessionStorage.removeItem(key);
      return true;
    } catch (error) {
      return false;
    }
  }

  function readHistory() {
    if (!storageAvailable()) return [];

    const raw = sessionStorage.getItem(STORAGE_KEY);
    const history = raw ? safeParse(raw) : [];

    return history
      .map((entry) => ({
        id: String(entry?.id || '').trim(),
        handle: String(entry?.handle || '').trim(),
        url: String(entry?.url || '').trim(),
        image: String(entry?.image || '').trim(),
        title: String(entry?.title || '').trim(),
        timestamp: Number(entry?.timestamp) || 0,
      }))
      .filter((entry) => entry.id && entry.handle && entry.url && entry.image);
  }

  function writeHistory(history) {
    if (!storageAvailable()) return;

    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(history.slice(0, MAX_STORED)));
    } catch (error) {
      // Ignore storage quota or privacy mode errors.
    }
  }

  function getProductRoot() {
    return document.querySelector('product-info[data-product-id]');
  }

  function getCurrentProduct() {
    const root = getProductRoot();
    if (!root) return null;

    const id = String(root.dataset.productId || '').trim();
    const handle = String(root.dataset.productHandle || '').trim();
    const url = String(root.dataset.productUrl || root.dataset.url || '').trim();
    const title = String(root.dataset.productTitle || document.title || '').trim();
    const image = String(root.dataset.productImage || '').trim();

    if (!id || !handle || !url || !image) return null;

    return {
      id,
      handle,
      url,
      image,
      title,
      timestamp: Date.now(),
    };
  }

  function isCurrentProduct(entry, currentProduct) {
    if (!entry || !currentProduct) return false;
    return String(entry.id) === String(currentProduct.id) || String(entry.handle).toLowerCase() === String(currentProduct.handle).toLowerCase();
  }

  function upsertCurrentProduct(currentProduct) {
    if (!currentProduct) return;

    const history = readHistory().filter((entry) => !isCurrentProduct(entry, currentProduct));
    history.unshift(currentProduct);
    writeHistory(history);
  }

  function getVisibleHistory() {
    const currentProduct = getCurrentProduct();
    const history = readHistory();

    return history.filter((entry) => !isCurrentProduct(entry, currentProduct)).slice(0, MAX_VISIBLE);
  }

  function isRecentHistoryEnabled() {
    if (!window.recentHistorySettings || typeof window.recentHistorySettings.enabled === 'undefined') {
      return true;
    }

    return Boolean(window.recentHistorySettings.enabled);
  }

  function isSystemOverlayOpen() {
    const body = document.body;
    if (!body) return false;

    if ([...body.classList].some((className) => className === 'overflow-hidden' || className.startsWith('overflow-hidden-'))) {
      return true;
    }

    const header = document.querySelector('.section-header');
    if (header && header.classList.contains('menu-open')) {
      return true;
    }

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cartDrawer.classList.contains('active')) {
      return true;
    }

    return false;
  }

  function ensureWidget() {
    if (state.widget) return state.widget;

    const widget = document.createElement('div');
    widget.className = 'recent-history';
    widget.setAttribute('aria-live', 'polite');
    widget.hidden = true;
    widget.innerHTML = `
      <button
        type="button"
        class="recent-history__button"
        aria-label="Recent products"
        aria-expanded="false"
        aria-controls="RecentHistoryPanel"
      >
        <span class="visually-hidden">Recent products</span>
        <span aria-hidden="true" class="recent-history__button-face">
          <span class="recent-history__button-image-wrap">
            <img class="recent-history__button-image" alt="" loading="eager" decoding="async" width="42" height="42">
          </span>
          <span class="recent-history__badge" hidden></span>
        </span>
      </button>
      <div class="recent-history__panel" id="RecentHistoryPanel" hidden>
        <div class="recent-history__list" data-recent-history-list></div>
      </div>
    `;

    document.body.appendChild(widget);

    const button = widget.querySelector('.recent-history__button');
    const buttonFace = widget.querySelector('.recent-history__button-face');
    const buttonImage = widget.querySelector('.recent-history__button-image');
    const badge = widget.querySelector('.recent-history__badge');
    const panel = widget.querySelector('.recent-history__panel');
    const list = widget.querySelector('[data-recent-history-list]');

    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (widget.hidden) return;
      setPanelOpen(!state.open);
    });

    button.addEventListener('pointerdown', () => {
      if (widget.hidden) return;
      button.classList.add('is-pressed');
    });
    button.addEventListener('pointerup', () => {
      button.classList.remove('is-pressed');
    });
    button.addEventListener('pointercancel', () => {
      button.classList.remove('is-pressed');
    });

    document.addEventListener('click', onDocumentClick, true);
    document.addEventListener('click', queueRender, true);
    document.addEventListener('keydown', onDocumentKeydown, true);
    document.addEventListener('keyup', queueRender, true);
    window.addEventListener('scroll', onWindowScroll, { passive: true });
    window.addEventListener('resize', queueRender, { passive: true });
    window.addEventListener('scroll', queueLayoutUpdate, { passive: true });
    window.addEventListener('resize', queueLayoutUpdate, { passive: true });
    window.addEventListener('orientationchange', queueLayoutUpdate, { passive: true });
    window.addEventListener('orientationchange', queueRender, { passive: true });
    document.addEventListener('shopify:section:load', queueRender);
    document.addEventListener('shopify:section:reorder', queueRender);
    document.addEventListener('DOMContentLoaded', queueRender);
    document.addEventListener('toggle', queueRender, true);
    window.addEventListener('pageshow', queueRender);

    state.widget = widget;
    state.button = button;
    state.buttonFace = buttonFace;
    state.buttonImage = buttonImage;
    state.badge = badge;
    state.panel = panel;
    state.list = list;

    return widget;
  }

  function setPanelOpen(isOpen) {
    state.open = Boolean(isOpen);

    if (!state.widget || state.widget.hidden) {
      state.open = false;
      return;
    }

    state.widget.classList.toggle('is-open', state.open);
    state.button.setAttribute('aria-expanded', String(state.open));

    if (state.panelHideTimer) {
      window.clearTimeout(state.panelHideTimer);
      state.panelHideTimer = null;
    }

    if (state.open) {
      state.panel.hidden = false;
      state.panel.classList.add('is-opening');
      window.requestAnimationFrame(() => {
        state.panel.classList.add('is-open');
        queueLayoutUpdate();
      });
      return;
    }

    state.panel.classList.remove('is-open');
    state.panel.classList.remove('is-opening');
    state.panelHideTimer = window.setTimeout(() => {
      if (state.panel) {
        state.panel.hidden = true;
      }
    }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 180);
  }

  function renderList(items) {
    if (!state.list) return;

    state.list.innerHTML = '';

    items.forEach((item, index) => {
      const link = document.createElement('a');
      link.className = 'recent-history__item';
      link.href = item.url;
      link.setAttribute('aria-label', item.title || 'Recent product');

      const image = document.createElement('img');
      image.className = 'recent-history__image';
      image.src = item.image;
      image.alt = item.title || '';
      image.loading = 'lazy';
      image.decoding = 'async';
      image.width = 50;
      image.height = 50;

      link.appendChild(image);
      link.style.setProperty('--recent-history-index', String(index));
      state.list.appendChild(link);
    });
  }

  function renderButton(items) {
    if (!state.buttonImage) return;

    const firstItem = items[0];
    if (!firstItem) {
      state.buttonImage.removeAttribute('src');
      state.buttonImage.removeAttribute('alt');
      return;
    }

    state.buttonImage.src = firstItem.image;
    state.buttonImage.alt = firstItem.title || '';
  }

  function updateWidgetVisibility() {
    const items = getVisibleHistory();
    const shouldShow = isRecentHistoryEnabled() && MOBILE_QUERY.matches && items.length > 0 && !isSystemOverlayOpen();

    if (!state.widget) ensureWidget();

    if (!state.widget) return;

    if (!shouldShow) {
      setPanelOpen(false);
      state.widget.hidden = true;
      state.widget.classList.remove('is-visible');
      state.widget.classList.remove('is-intro');
      state.lastSignature = '';
      state.visible = false;
      if (state.badge) state.badge.hidden = true;
      return;
    }

    const signature = items.map((item) => item.id).join('|');
    if (signature !== state.lastSignature) {
      renderList(items);
      renderButton(items);
      state.lastSignature = signature;
    } else {
      renderButton(items);
    }

    if (!state.visible) {
      state.widget.classList.add('is-intro');
      if (state.introTimer) {
        window.clearTimeout(state.introTimer);
      }
      state.introTimer = window.setTimeout(() => {
        state.widget && state.widget.classList.remove('is-intro');
      }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 450);
    }

    state.widget.hidden = false;
    state.widget.classList.add('is-visible');
    state.visible = true;
    if (state.badge) {
      state.badge.hidden = false;
      state.badge.textContent = String(items.length);
    }
    queueLayoutUpdate();
  }

  function queueRender() {
    if (state.renderQueued) return;
    state.renderQueued = true;

    window.clearTimeout(state.renderTimer);
    state.renderTimer = window.setTimeout(() => {
      state.renderQueued = false;
      render();
    }, 50);
  }

  function getHeaderAnchor() {
    const candidates = [
      document.querySelector('.section-header'),
      document.querySelector('.shopify-section-group-header-group'),
      document.querySelector('.header-wrapper'),
    ].filter(Boolean);

    let bestCandidate = null;
    let bestBottom = -1;

    for (const candidate of candidates) {
      if (!candidate.getClientRects().length) continue;
      const bottom = candidate.getBoundingClientRect().bottom;
      if (bottom > bestBottom) {
        bestBottom = bottom;
        bestCandidate = candidate;
      }
    }

    return bestCandidate;
  }

  function updateLayout() {
    state.layoutQueued = false;

    if (!document.documentElement) return;

    const headerAnchor = getHeaderAnchor();
    const bottom = headerAnchor ? Math.max(0, Math.round(headerAnchor.getBoundingClientRect().bottom)) : 0;
    document.documentElement.style.setProperty('--recent-history-top', `${bottom + 16}px`);
  }

  function queueLayoutUpdate() {
    if (state.layoutQueued) return;
    state.layoutQueued = true;

    if (state.layoutTimer) {
      window.cancelAnimationFrame(state.layoutTimer);
    }
    state.layoutTimer = window.requestAnimationFrame(updateLayout);
  }

  function render() {
    if (!document.body || !document.body.classList.contains('template-product')) {
      if (state.widget) {
        setPanelOpen(false);
        state.widget.hidden = true;
        state.widget.classList.remove('is-visible');
      }
      return;
    }

    const currentProduct = getCurrentProduct();
    if (currentProduct) {
      upsertCurrentProduct(currentProduct);
    }

    if (!isRecentHistoryEnabled()) {
      if (state.widget) {
        setPanelOpen(false);
        state.widget.hidden = true;
        state.widget.classList.remove('is-visible', 'is-intro');
      }
      return;
    }

    updateLayout();
    queueLayoutUpdate();
    updateWidgetVisibility();
  }

  function onDocumentClick(event) {
    if (!state.widget || state.widget.hidden || !state.open) return;
    if (state.widget.contains(event.target)) return;
    setPanelOpen(false);
  }

  function onDocumentKeydown(event) {
    if (event.code && event.code.toUpperCase() === 'ESCAPE') {
      setPanelOpen(false);
    }
  }

  function onWindowScroll() {
    if (state.open) {
      setPanelOpen(false);
    }
    queueLayoutUpdate();
  }

  function init() {
    if (!document.body || !document.body.classList.contains('template-product')) {
      return;
    }

    if (state.widget) {
      queueRender();
      return;
    }

    ensureWidget();
    updateLayout();
    render();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
